import { useCallback, useEffect, useState } from "react";
import useAutosaveKey from "./useAutosaveKey";

const useAutosaveValue = <T>(
  formKey: string,
  defaultValue: T,
  overwrite?: (savedValue: T) => T,
  debounceMs: number = 1000
): [T, () => void, () => void] => {
  const [savedValue, setSavedValue] = useState<T>(defaultValue);
  const autosaveKey = useAutosaveKey(formKey);

  const clearSavedValue = useCallback(() => {
    setSavedValue(defaultValue);
    setTimeout(() => {
      localStorage.removeItem(autosaveKey);
    }, debounceMs);
  }, [autosaveKey, debounceMs, defaultValue]);

  const loadAutosavedValues = useCallback(() => {
    const ISODateRegEx = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*))(?:Z|(\+|-)([\d|:]*))?$/;
    const savedStringValue = localStorage.getItem(autosaveKey);
    if (savedStringValue !== null) {
      const parsedJSON = JSON.parse(savedStringValue, (key, value) => {
        if (typeof value === "string" && ISODateRegEx.exec(value)) {
          return new Date(value);
        }

        return value;
      }) as T;
      const newValue = overwrite === undefined ? parsedJSON : overwrite(parsedJSON);
      setSavedValue(newValue);
    }
  }, [autosaveKey, overwrite]);

  const reloadAutosavedValues = useCallback(() => {
    setTimeout(() => {
      loadAutosavedValues();
    }, debounceMs);
  }, [debounceMs, loadAutosavedValues]);

  useEffect(() => {
    loadAutosavedValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [savedValue, clearSavedValue, reloadAutosavedValues];
};

export default useAutosaveValue;
